import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  GiftReofferProductEditor,
  type GiftReofferSelections,
} from '@/components/gifts/GiftReofferProductEditor';
import {
  giftReofferProductFromShopifyProduct,
  refreshGiftReofferAddedProducts,
  withGiftReofferProductSelectionStatus,
} from '@/components/gifts/gift-reoffer-stock';
import { COMPANION_PATH, invalidateCompanionQueries } from '@/hooks/queries/companion-keys';
import { invalidateGiftQueries } from '@/hooks/queries/gift-keys';
import {
  giftReofferReviewKey,
  refreshGiftReofferReviews,
  shouldRefreshGiftReofferReview,
} from '@/hooks/queries/use-gift-reoffer-review-query';
import { useCurrentBrandQuery } from '@/hooks/queries/use-current-brand-query';
import type { ShopifyProduct } from '@/hooks/queries/use-shopify-products-query';
import api from '@/libs/axiosInstance';
import { useCompanionPanelStore } from '@/stores/companion-panel-store';
import { Alert, Box, Button, Checkbox, Spinner, Stack, Text, VStack } from '@chakra-ui/react';
import {
  GiftReofferCatalogStatus,
  ErrorCode,
  type ApiResponse,
  type CompanionGiftReofferExecuteResponse,
  type GiftReofferProductSelection,
  type GiftReofferReview,
  type GiftReofferReviewProduct,
} from '@fluenceur/types';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

type Selection = Record<string, GiftReofferProductSelection[]>;
type ExecuteError = AxiosError<ApiResponse<CompanionGiftReofferExecuteResponse>>;
type ExecuteVariables = {
  actionId: string;
  version: number;
  items: {
    giftRequestId: string;
    products: GiftReofferProductSelection[];
    allowOutOfStock: boolean;
  }[];
};

export const MAX_GIFT_REOFFER_BATCH_SIZE = 50;

/** A retry has to repeat only the targets the server explicitly refused. */
export const retryFailedGiftRequestIds = (
  results: CompanionGiftReofferExecuteResponse['results']
) => results.filter((result) => result.outcome === 'failed').map((result) => result.giftRequestId);

const selectionsFor = (review: GiftReofferReview): GiftReofferProductSelection[] =>
  review.products.map((product) => ({
    shopifyProductId: product.shopifyProductId,
    shopifyVariantIds: product.variants.map((variant) => variant.shopifyVariantId),
  }));

const batchesOf = <T,>(items: T[], size: number): T[][] => {
  const batches: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    batches.push(items.slice(index, index + size));
  }
  return batches;
};

const failedBatchResults = (
  items: ExecuteVariables['items'],
  error: ExecuteError
): CompanionGiftReofferExecuteResponse['results'] =>
  items.map((item) => ({
    giftRequestId: item.giftRequestId,
    outcome: 'failed',
    successorGiftRequestId: null,
    errorCode: error.response?.data?.code ?? ErrorCode.E_GIFT_REOFFER_NOT_ALLOWED,
  }));

/** A 409 response carries the latest action snapshot without discovering new targets. */
const versionFromError = (error: ExecuteError): number | null => {
  const payload = error.response?.data?.data as { action?: unknown } | undefined;
  const candidate = payload?.action;
  if (
    typeof candidate === 'object' &&
    candidate !== null &&
    'version' in candidate &&
    typeof candidate.version === 'number'
  ) {
    return candidate.version;
  }
  return null;
};

/**
 * The batch review uses the ids frozen into the card. It never discovers a
 * replacement target from the current gift list, which prevents a stale card
 * from silently re-offering a different person's gift.
 */
export const CompanionGiftReofferCtaHost = () => {
  const { t } = useTranslation();
  const request = useCompanionPanelStore((state) => state.giftReofferCta);
  const close = useCompanionPanelStore((state) => state.closeGiftReofferCta);
  const queryClient = useQueryClient();
  const { data: brand } = useCurrentBrandQuery();
  const brandId = brand?.id ?? '';
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selection, setSelection] = useState<Selection>({});
  const [addedProducts, setAddedProducts] = useState<Record<string, GiftReofferReviewProduct[]>>(
    {}
  );
  const [removedProductIds, setRemovedProductIds] = useState<Record<string, string[]>>({});
  const [allowOutOfStock, setAllowOutOfStock] = useState(false);
  const [results, setResults] = useState<CompanionGiftReofferExecuteResponse['results'] | null>(
    null
  );
  const [isRefreshingCatalog, setIsRefreshingCatalog] = useState(false);
  const [catalogRefreshFailed, setCatalogRefreshFailed] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [actionVersion, setActionVersion] = useState<number | null>(null);
  const seededActionId = useRef<string | null>(null);
  const executing = useRef(false);
  const ids = request?.giftRequestIds ?? [];
  const reviews = useQueries({
    queries: ids.map((giftId) => ({
      queryKey: giftReofferReviewKey(brandId, giftId),
      queryFn: async (): Promise<GiftReofferReview> => {
        const { data } = await api.get<ApiResponse<GiftReofferReview>>(
          `/dashboard/gifts/${giftId}/reoffer-review`
        );
        if (!data.data) throw new Error(data.message || 'Missing reoffer review');
        return data.data;
      },
      enabled: Boolean(request && brandId),
    })),
  });
  const loadedReviews = reviews
    .map((query) => query.data)
    .filter((review): review is GiftReofferReview => Boolean(review));
  const loadedReviewCount = loadedReviews.length;
  const recipientNames = new Map(
    loadedReviews.map((review) => [review.sourceGiftId, review.recipient.displayName])
  );
  const requestRef = useRef(request);
  const idsRef = useRef(ids);
  const loadedReviewsRef = useRef(loadedReviews);
  requestRef.current = request;
  idsRef.current = ids;
  loadedReviewsRef.current = loadedReviews;

  useEffect(() => {
    const activeRequest = requestRef.current;
    const targetIds = idsRef.current;
    const reviewsForSeed = loadedReviewsRef.current;
    if (!activeRequest) {
      seededActionId.current = null;
      setIsRefreshingCatalog(false);
      setCatalogRefreshFailed(false);
      setActionVersion(null);
      return;
    }
    if (seededActionId.current === activeRequest.actionId) return;
    if (reviewsForSeed.length !== targetIds.length) return;
    setSelectedIds(targetIds);
    setSelection(
      Object.fromEntries(
        reviewsForSeed.map((review) => [review.sourceGiftId, selectionsFor(review)])
      )
    );
    setAddedProducts({});
    setRemovedProductIds({});
    setAllowOutOfStock(false);
    setResults(null);
    setIsRefreshingCatalog(false);
    setCatalogRefreshFailed(false);
    setActionVersion(activeRequest.version);
    seededActionId.current = activeRequest.actionId;
  }, [request?.actionId, loadedReviewCount]);

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const selectedReviews = useMemo(
    () =>
      reviews
        .map((query) => query.data)
        .filter((review): review is GiftReofferReview =>
          Boolean(review && selectedIdSet.has(review.sourceGiftId))
        ),
    [reviews, selectedIdSet]
  );
  const selectedVariants = (review: GiftReofferReview, productId: string) =>
    selection[review.sourceGiftId]?.find((product) => product.shopifyProductId === productId)
      ?.shopifyVariantIds ?? [];
  const editorSelections = (giftRequestId: string): GiftReofferSelections =>
    Object.fromEntries(
      (selection[giftRequestId] ?? []).map((product) => [
        product.shopifyProductId,
        product.shopifyVariantIds,
      ])
    );
  const updateEditorSelections = (giftRequestId: string, next: GiftReofferSelections) =>
    setSelection((current) => ({
      ...current,
      [giftRequestId]: Object.entries(next).map(([shopifyProductId, shopifyVariantIds]) => ({
        shopifyProductId,
        shopifyVariantIds,
      })),
    }));
  const productsForReview = (review: GiftReofferReview) =>
    [...review.products, ...(addedProducts[review.sourceGiftId] ?? [])]
      .filter(
        (product) =>
          !(removedProductIds[review.sourceGiftId] ?? []).includes(product.shopifyProductId)
      )
      .map((product) =>
        withGiftReofferProductSelectionStatus(
          product,
          selectedVariants(review, product.shopifyProductId)
        )
      );
  const addProduct = (giftRequestId: string, product: ShopifyProduct) => {
    const review = loadedReviews.find((candidate) => candidate.sourceGiftId === giftRequestId);
    const reviewedProduct = review?.products.find(
      (candidate) => candidate.shopifyProductId === product.id
    );
    if (reviewedProduct) {
      setRemovedProductIds((current) => ({
        ...current,
        [giftRequestId]: (current[giftRequestId] ?? []).filter((id) => id !== product.id),
      }));
      setSelection((current) => ({
        ...current,
        [giftRequestId]: [
          ...(current[giftRequestId] ?? []),
          {
            shopifyProductId: reviewedProduct.shopifyProductId,
            shopifyVariantIds: reviewedProduct.variants.map((variant) => variant.shopifyVariantId),
          },
        ],
      }));
      return;
    }
    setAddedProducts((current) => {
      if ((current[giftRequestId] ?? []).some((entry) => entry.shopifyProductId === product.id))
        return current;
      const next = giftReofferProductFromShopifyProduct(product);
      setSelection((selected) => ({
        ...selected,
        [giftRequestId]: [
          ...(selected[giftRequestId] ?? []),
          {
            shopifyProductId: next.shopifyProductId,
            shopifyVariantIds: next.variants.map((variant) => variant.shopifyVariantId),
          },
        ],
      }));
      return { ...current, [giftRequestId]: [...(current[giftRequestId] ?? []), next] };
    });
  };
  const hasInvalid = selectedReviews.some(
    (review) =>
      review.blockingReferences.some((reference) => {
        const variants = selectedVariants(review, reference.shopifyProductId);
        return (
          variants.length > 0 &&
          (!reference.shopifyVariantId || variants.includes(reference.shopifyVariantId))
        );
      }) ||
      productsForReview(review).some(
        (product) =>
          product.status === GiftReofferCatalogStatus.MISSING ||
          product.status === GiftReofferCatalogStatus.NOT_ORDERABLE
      )
  );
  const hasStockWarning = selectedReviews.some((review) =>
    productsForReview(review).some((product) => {
      const variants = selectedVariants(review, product.shopifyProductId);
      return (
        variants.length > 0 &&
        product.variants.some(
          (variant) =>
            variants.includes(variant.shopifyVariantId) &&
            variant.status === GiftReofferCatalogStatus.OUT_OF_STOCK
        )
      );
    })
  );
  const selectedReviewQueries = reviews.filter((_query, index) => selectedIdSet.has(ids[index]));
  const areSelectedReviewsReady =
    selectedIds.length > 0 &&
    selectedReviewQueries.length === selectedIds.length &&
    selectedReviewQueries.every(
      (query) => Boolean(query?.data) && !query.isFetching && !query.isError
    );
  const hasConfiguredProducts =
    areSelectedReviewsReady &&
    selectedReviews.every((review) =>
      (selection[review.sourceGiftId] ?? []).some((product) => product.shopifyVariantIds.length > 0)
    );
  const refreshAddedProducts = async (giftIds: string[]) => {
    const targetIds = new Set(giftIds);
    const refreshed = await Promise.all(
      Object.entries(addedProducts)
        .filter(([giftId]) => targetIds.has(giftId))
        .map(async ([giftId, products]) => [
          giftId,
          await refreshGiftReofferAddedProducts(queryClient, products),
        ])
    );
    if (refreshed.length > 0) {
      setAddedProducts((current) => ({ ...current, ...Object.fromEntries(refreshed) }));
    }
  };
  const recoverCatalog = async (giftIds: string[]) => {
    setIsRefreshingCatalog(true);
    setCatalogRefreshFailed(false);
    try {
      await Promise.all([
        refreshGiftReofferReviews(queryClient, brandId, giftIds),
        refreshAddedProducts(giftIds),
      ]);
      return true;
    } catch {
      setCatalogRefreshFailed(true);
      return false;
    } finally {
      setIsRefreshingCatalog(false);
    }
  };
  const mutation = useMutation<CompanionGiftReofferExecuteResponse, ExecuteError, ExecuteVariables>(
    {
      mutationFn: async ({ actionId, version, items }) => {
        const { data } = await api.post<ApiResponse<CompanionGiftReofferExecuteResponse>>(
          `${COMPANION_PATH}/actions/${actionId}/gift-reoffers`,
          {
            version,
            items,
          }
        );
        if (!data.data)
          throw new Error(data.message || 'Missing companion reoffer response') as ExecuteError;
        return data.data;
      },
    }
  );
  const executeBatches = async () => {
    if (!request || executing.current) return;
    executing.current = true;

    // Freeze every target and its reviewed selection before the first request.
    // Later batches must never read the live card, query cache, or edited UI.
    const items: ExecuteVariables['items'] = selectedIds.map((giftRequestId) => ({
      giftRequestId,
      products: (selection[giftRequestId] ?? []).filter(
        (product) => product.shopifyVariantIds.length > 0
      ),
      allowOutOfStock,
    }));
    let version = actionVersion ?? request.version;
    const aggregated: CompanionGiftReofferExecuteResponse['results'] = [];

    setIsExecuting(true);
    try {
      for (const batch of batchesOf(items, MAX_GIFT_REOFFER_BATCH_SIZE)) {
        try {
          const response = await mutation.mutateAsync({
            actionId: request.actionId,
            version,
            items: batch,
          });
          version =
            typeof response.action?.version === 'number' ? response.action.version : version;
          aggregated.push(...response.results);
        } catch (error) {
          const executeError = error as ExecuteError;
          // A transport/lost-response failure is deliberately visible for each
          // frozen target. Retrying is safe because the server journals decisions.
          version = versionFromError(executeError) ?? version;
          aggregated.push(...failedBatchResults(batch, executeError));
        }
      }

      const rejectedGiftIds = aggregated
        .filter(
          (result) =>
            result.outcome === 'failed' &&
            shouldRefreshGiftReofferReview(result.errorCode as ErrorCode | undefined)
        )
        .map((result) => result.giftRequestId);
      if (rejectedGiftIds.length > 0) await recoverCatalog(rejectedGiftIds);

      // Results become visible exactly once: after all frozen batches settled.
      setActionVersion(version);
      setResults(aggregated);
      invalidateCompanionQueries(queryClient, brandId);
      invalidateGiftQueries(queryClient, brandId);
    } finally {
      executing.current = false;
      setIsExecuting(false);
    }
  };
  const retryIds = results ? retryFailedGiftRequestIds(results) : [];
  const isLoading = reviews.some((query) => query.isLoading) || isRefreshingCatalog;
  const hasError = reviews.some((query) => query.isError) || catalogRefreshFailed;
  const canConfirm =
    selectedIds.length > 0 &&
    areSelectedReviewsReady &&
    hasConfiguredProducts &&
    !hasInvalid &&
    (!hasStockWarning || allowOutOfStock) &&
    !isRefreshingCatalog &&
    !catalogRefreshFailed &&
    !isExecuting;

  return (
    <DialogRoot
      open={Boolean(request)}
      onOpenChange={(details) => !details.open && !isExecuting && close()}
      placement="center"
      scrollBehavior="inside"
      closeOnEscape={!isExecuting}
      closeOnInteractOutside={!isExecuting}
    >
      <DialogContent
        maxW={{ base: 'calc(100vw - 24px)', md: 'xl' }}
        maxH="calc(100dvh - 48px)"
        overflow="hidden"
        display="flex"
        flexDirection="column"
        data-testid="companion-gift-reoffer-review"
      >
        <DialogHeader>
          <DialogTitle>{t('companion.giftReoffer.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody flex="1" minH="0" overflowY="auto">
          {isLoading ? <Spinner /> : null}
          {hasError ? (
            <Alert.Root status="error">
              <Alert.Indicator />
              <VStack align="start">
                <Alert.Title>{t('companion.giftReoffer.unavailable')}</Alert.Title>
                <Button
                  size="sm"
                  loading={isRefreshingCatalog}
                  onClick={() => {
                    if (catalogRefreshFailed) void recoverCatalog(selectedIds);
                    else reviews.forEach((query) => query.refetch());
                  }}
                >
                  {t('common.retry')}
                </Button>
              </VStack>
            </Alert.Root>
          ) : null}
          {!isLoading && !hasError && !results ? (
            <Stack gap="3">
              {reviews.map((query) =>
                query.data ? (
                  <Box key={query.data.sourceGiftId} borderWidth="1px" borderRadius="md" p="3">
                    <Checkbox.Root
                      checked={selectedIdSet.has(query.data.sourceGiftId)}
                      onCheckedChange={(details) =>
                        setSelectedIds((current) =>
                          details.checked
                            ? [...current, query.data!.sourceGiftId]
                            : current.filter((id) => id !== query.data!.sourceGiftId)
                        )
                      }
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control />
                      <Checkbox.Label>
                        {t('companion.giftReoffer.item', {
                          name: query.data.recipient.displayName,
                        })}
                      </Checkbox.Label>
                    </Checkbox.Root>
                    {selectedIdSet.has(query.data.sourceGiftId) ? (
                      <Box mt="2" pl="6">
                        <GiftReofferProductEditor
                          products={productsForReview(query.data)}
                          selections={editorSelections(query.data.sourceGiftId)}
                          onChange={(next) =>
                            updateEditorSelections(query.data!.sourceGiftId, next)
                          }
                          onRemove={(productId) => {
                            const giftRequestId = query.data!.sourceGiftId;
                            setAddedProducts((current) => ({
                              ...current,
                              [giftRequestId]: (current[giftRequestId] ?? []).filter(
                                (product) => product.shopifyProductId !== productId
                              ),
                            }));
                            setRemovedProductIds((current) => ({
                              ...current,
                              [giftRequestId]: (current[giftRequestId] ?? []).includes(productId)
                                ? current[giftRequestId]
                                : [...(current[giftRequestId] ?? []), productId],
                            }));
                            setSelection((current) => ({
                              ...current,
                              [giftRequestId]: (current[giftRequestId] ?? []).filter(
                                (product) => product.shopifyProductId !== productId
                              ),
                            }));
                          }}
                          onAddProduct={(product) => addProduct(query.data!.sourceGiftId, product)}
                          addLabel={t('gifting.drawer.reoffer.addProduct')}
                          removeLabel={t('common.remove')}
                          statusLabel={(product) =>
                            product.status === GiftReofferCatalogStatus.MISSING ||
                            product.status === GiftReofferCatalogStatus.NOT_ORDERABLE
                              ? t('gifting.drawer.reoffer.productInvalid')
                              : product.status === GiftReofferCatalogStatus.OUT_OF_STOCK
                                ? t('gifting.drawer.reoffer.productOutOfStock')
                                : t('gifting.drawer.reoffer.productAvailable')
                          }
                        />
                      </Box>
                    ) : null}
                  </Box>
                ) : null
              )}
              {hasInvalid ? (
                <Alert.Root status="error">
                  <Alert.Indicator />
                  <Alert.Title>{t('companion.giftReoffer.invalid')}</Alert.Title>
                </Alert.Root>
              ) : null}
              {hasStockWarning ? (
                <Checkbox.Root
                  checked={allowOutOfStock}
                  onCheckedChange={(details) => setAllowOutOfStock(Boolean(details.checked))}
                >
                  <Checkbox.HiddenInput />
                  <Checkbox.Control />
                  <Checkbox.Label>{t('companion.giftReoffer.allowOutOfStock')}</Checkbox.Label>
                </Checkbox.Root>
              ) : null}
            </Stack>
          ) : null}
          {results ? (
            <Stack gap="2">
              {results.map((result) => (
                <Text key={result.giftRequestId}>
                  {t(
                    result.outcome === 'failed'
                      ? 'companion.giftReoffer.failed'
                      : 'companion.giftReoffer.completed',
                    {
                      id:
                        recipientNames.get(result.giftRequestId) ??
                        t('companion.cards.giftExpiredReoffer.unknownAffiliate'),
                    }
                  )}
                </Text>
              ))}
            </Stack>
          ) : null}
        </DialogBody>
        <DialogFooter flexShrink="0">
          <Button variant="outline" onClick={close} disabled={isExecuting}>
            {t('common.close')}
          </Button>
          {results && retryIds.length && !isExecuting ? (
            <Button
              onClick={() => {
                setSelectedIds(retryIds);
                setResults(null);
              }}
            >
              {t('companion.giftReoffer.retryFailures')}
            </Button>
          ) : null}
          {!results ? (
            <Button
              onClick={() => void executeBatches()}
              loading={isExecuting}
              disabled={!canConfirm}
            >
              {t('companion.giftReoffer.confirm')}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
